import React from 'react';

import { parseMarkdownLocationLink } from '@aline/common';
import ReactMarkdown from 'react-markdown';
import { Citation, counterResetStyles } from 'src/components/ai/shared/components/Citation';
import { CitationCard } from 'src/components/ai/shared/components/CitationCard';
import { openLink, useMaybeDocumentParams } from 'src/helpers';
import { useGetDocumentHeader } from 'src/hooks/document';
import { makeStyles } from 'tss-react/mui';

import { usePlaybookNavigate } from './usePlaybookNavigate';

const useStyles = makeStyles()(() => ({
  container: {
    '& p': {
      margin: 0,
    },
    '& p + p': {
      marginTop: 8,
    },
    ...counterResetStyles,
  },
}));

/**
 * The anchor renderer for explanation markdown. Location links (`#doc/…`)
 * become numbered citation chips that scroll to and select the cited
 * paragraph — the same presentational chip chat citations use, minus the
 * chat-thread bookkeeping. Anything else is an ordinary link (a rule citing
 * an external policy URL, say) and opens outside the editor.
 */
function ExplanationAnchor({
  children,
  href,
}: {
  children?: React.ReactNode;
  href?: string;
}): JSX.Element | null {
  const { navigateToCardAnchor } = usePlaybookNavigate();
  const { documentId } = useMaybeDocumentParams();
  const locationParts = href?.startsWith('#') ? parseMarkdownLocationLink(href) : null;
  const { document: citedDocument } = useGetDocumentHeader(locationParts?.documentId ?? null);
  if (!href) return null;

  if (locationParts) {
    const location = locationParts;
    const citesThisDocument = location.documentId === documentId;

    function openCitation() {
      navigateToCardAnchor({
        documentId: location.documentId,
        locationAnchor: location.locationId,
      });
    }

    return (
      <Citation
        card={
          <CitationCard
            caption={citesThisDocument ? 'This document' : null}
            jumpInPlace={citesThisDocument}
            title={citedDocument?.name ?? ''}
            onViewInDocument={openCitation}
          />
        }
        tooltip={
          citedDocument?.name || (citesThisDocument ? 'View citation' : 'Open referenced document')
        }
        onClickCitation={event => {
          event.preventDefault();
          event.stopPropagation();
          openCitation();
        }}
      />
    );
  }
  // A `#` link that is not a valid location cites nothing — drop it.
  if (href.startsWith('#')) return null;

  return (
    <a
      href={href}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        openLink(href);
      }}
    >
      {children}
    </a>
  );
}

/**
 * Renders a position result's explanation: markdown whose citations were
 * validated server-side at run time (`convertCitationsWithParagraphState`),
 * so every location link points at a paragraph that existed when the run
 * read the document.
 */
export const PlaybookExplanationMarkdown = React.memo(function PlaybookExplanationMarkdown({
  markdown,
}: {
  markdown: string;
}): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.container}>
      <ReactMarkdown components={{ a: ExplanationAnchor }}>{markdown}</ReactMarkdown>
    </div>
  );
});
